import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';
import { CategoryMenuItem, CategoryMenuLogicReturn } from './CategoryMenu.types';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchProducts, setSelectedCategoryId, fetchCategoryDetail, fetchCategoryTree } from '../../../redux/WithcookieSlice';

/**
 * 카테고리 메뉴 비즈니스 로직 훅
 */
const useCategoryMenuLogic = (
  componentData: ComponentData,
  mode: ComponentRenderMode
): CategoryMenuLogicReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { componentProps = {} } = componentData;
  
  // 경로에서 마지막 세그먼트 추출 함수
  const extractLastPathSegment = (pathname: string): string | null => {
    // /shopping 경로 제거
    const pathWithoutShopping = pathname.replace('/shopping', '');
    
    // 빈 경로이거나 '/' 만 있으면 null 반환 (루트)
    if (!pathWithoutShopping || pathWithoutShopping === '/') {
      return null;
    }
    
    // 마지막 세그먼트 추출
    const segments = pathWithoutShopping.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : null;
  };
  
  // 경로에서 모든 세그먼트 추출 함수 (계층 구조 지원)
  const extractAllPathSegments = (pathname: string): string[] => {
    // /shopping 경로 제거
    const pathWithoutShopping = pathname.replace('/shopping', '');
    
    // 빈 경로이거나 '/' 만 있으면 빈 배열 반환
    if (!pathWithoutShopping || pathWithoutShopping === '/') {
      return [];
    }
    
    // 모든 세그먼트 추출
    return pathWithoutShopping.split('/').filter(Boolean);
  };
  
  // 경로 세그먼트로 카테고리 ID 찾기
  const getCategoryIdFromPath = (pathSegment: string): number | null => {
    if (!pathSegment || Object.keys(categoryNameToIdMap).length === 0) {
      return null;
    }
    
    // 1. 원본 그대로 시도
    let result = categoryNameToIdMap[pathSegment];
    
    // 2. URL 디코딩된 버전으로 시도
    if (!result) {
      const decodedSegment = decodeURIComponent(pathSegment);
      result = categoryNameToIdMap[decodedSegment];
      
      // 3. 디코딩된 버전을 URL 친화적으로 변환해서 시도
      if (!result) {
        const urlFriendlyDecoded = decodedSegment
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^가-힣A-Za-z0-9\-]/g, '')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
        result = categoryNameToIdMap[urlFriendlyDecoded];
      }
    }
    

    return result;
  };
  
  // Redux에서 카테고리 상세 정보 및 트리 가져오기
  const currentCategoryDetail = useSelector((state: any) => state.withcookie?.currentCategoryDetail);
  const categoryNameToIdMap = useSelector((state: any) => state.withcookie?.categoryNameToIdMap || {});
  const categoryTreeLoading = useSelector((state: any) => state.withcookie?.categoryTreeLoading || false);
  
  // 상태 관리
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [breadcrumb, setBreadcrumb] = useState<Array<{id: number; name: string; parentId: number | null}>>([]);
  const [currentChildren, setCurrentChildren] = useState<CategoryMenuItem[]>([]);
  const [lastLoadedCategoryId, setLastLoadedCategoryId] = useState<number | null>(null);
  
  // props에서 필요한 값 추출 (URL 기반 시스템에서는 menuItems 불필요)
  
  // 컴포넌트 마운트 시 카테고리 트리 로드 (한번만)
  useEffect(() => {
    if (Object.keys(categoryNameToIdMap).length === 0 && !categoryTreeLoading) {
      dispatch(fetchCategoryTree() as any);
    }
  }, [dispatch]);
  
  // 경로 변경 시 카테고리 상세 정보 로드
  useEffect(() => {
    // 카테고리 매핑이 준비되지 않았으면 대기
    if (Object.keys(categoryNameToIdMap).length === 0) {
      return;
    }
    
    // 현재 경로에서 모든 세그먼트 추출 (계층 구조 지원)
    const allSegments = extractAllPathSegments(location.pathname);
    const lastSegment = allSegments.length > 0 ? allSegments[allSegments.length - 1] : null;
    

    if (!lastSegment) {
      // 루트 경로: 전체 상품 표시
      if (lastLoadedCategoryId === null) {
        return;
      }
      
      setLastLoadedCategoryId(null);
      setBreadcrumb([]);
      setCurrentChildren([]);
      setSelectedItemId(null);
      dispatch(setSelectedCategoryId(null));
      
      dispatch(fetchProducts({
        include_category_ids: [],
        page: 1,
        per_page: 20
      }) as any);
      
      return;
    }
    
    // 계층 구조를 고려한 카테고리 ID 찾기
    // 먼저 전체 경로로 시도하고, 없으면 마지막 세그먼트만으로 시도
    let categoryId = null;
    
    // 전체 경로를 조합해서 찾기 (예: "셔틀콕/일반용-셔틀콕")
    if (allSegments.length > 1) {
      const fullPath = allSegments.join('/');
      categoryId = getCategoryIdFromPath(fullPath);
    }
    
    // 전체 경로로 못 찾았으면 마지막 세그먼트만으로 찾기
    if (!categoryId) {
      categoryId = getCategoryIdFromPath(lastSegment);
    }
    
    if (categoryId) {
      // 이미 로드된 카테고리인지 확인하여 중복 호출 방지
      if (lastLoadedCategoryId === categoryId) {
        return;
      }
      
      // 카테고리가 발견되면 상세 정보 로드
      setLastLoadedCategoryId(categoryId);
      setSelectedItemId(`backend-cat-${categoryId}`);
      dispatch(setSelectedCategoryId(categoryId));
      dispatch(fetchCategoryDetail(categoryId) as any);
      
      dispatch(fetchProducts({
        include_category_ids: [categoryId],
        page: 1,
        per_page: 20
      }) as any);
    } else {
      // 매칭되는 카테고리가 없으면 전체 표시
      if (lastLoadedCategoryId === null) {
        return;
      }
      
      setLastLoadedCategoryId(null);
      setBreadcrumb([]);
      setCurrentChildren([]);
      setSelectedItemId(null);
      dispatch(setSelectedCategoryId(null));
      
      dispatch(fetchProducts({
        include_category_ids: [],
        page: 1,
        per_page: 20
      }) as any);
    }
  }, [location.pathname, categoryNameToIdMap, lastLoadedCategoryId]);
  
  // 카테고리 상세 정보가 변경될 때 breadcrumb과 currentChildren 업데이트
  useEffect(() => {
    if (currentCategoryDetail) {
      // breadcrumb 업데이트
      setBreadcrumb(currentCategoryDetail.breadcrumb || []);
      
      // currentChildren을 CategoryMenuItem 형식으로 변환
      // 백엔드에서 불러온 하위 카테고리들의 라우팅을 자동 생성
      const convertedChildren: CategoryMenuItem[] = (currentCategoryDetail.children || []).map((child: any) => {
        // 라우팅 경로 생성: 카테고리 이름을 URL 친화적으로 변환
        const urlFriendlyName = child.name
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^가-힣A-Za-z0-9\-]/g, '')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
        
        // 전체 계층 경로 구성 - currentCategoryDetail.breadcrumb 사용
        let fullRoutingPath = '';
        
        if (currentCategoryDetail.breadcrumb && currentCategoryDetail.breadcrumb.length > 0) {
          // breadcrumb의 각 항목을 URL 친화적으로 변환하여 경로 구성
          const pathSegments = currentCategoryDetail.breadcrumb.map((crumb: any) => {
            return crumb.name
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/[^가-힣A-Za-z0-9\-]/g, '')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '');
          });
          
          // 현재 하위 카테고리도 추가
          pathSegments.push(urlFriendlyName || child.id.toString());
          
          fullRoutingPath = '/' + pathSegments.join('/');
        } else {
          // breadcrumb이 없으면 단일 세그먼트 사용
          fullRoutingPath = `/${urlFriendlyName || child.id}`;
        }
        

        return {
          id: `backend-cat-${child.id}`, // 백엔드 카테고리임을 명시하여 웹빌더 menuItems와 구분
          categoryId: child.id,
          categoryName: child.name,
          routingPath: fullRoutingPath,
          parentId: child.parentId ? `backend-cat-${child.parentId}` : null,
          order: child.orderNum || 0,
          visible: true,
          children: []
        };
      });
      
      setCurrentChildren(convertedChildren);
    }
  }, [currentCategoryDetail]);
  
  // 메뉴 아이템 클릭 핸들러 - 라우팅 기반으로 변경
  const handleMenuItemClick = useCallback((item: CategoryMenuItem) => {
    // 모든 모드에서 클릭 허용 - 에디터/프리뷰 구분 없이 동작
    
    // 라우팅 경로가 비어있으면 경고
    if (!item.routingPath) {
      console.warn('⚠️ 라우팅 경로가 설정되지 않았습니다');
      return;
    }
    
    // 라우팅만 실행 - 나머지는 useEffect에서 처리
    const fullPath = `/shopping${item.routingPath}`;
    navigate(fullPath, {
      state: {
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        menuItemId: item.id,
        routingPath: item.routingPath
      }
    });
  }, [mode, navigate]);
  
  // 확장/축소 토글
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  // 루트(전체)로 되돌아가는 함수
  const handleBackToRoot = useCallback(() => {
    // 모든 모드에서 클릭 허용 - 에디터/프리뷰 구분 없이 동작
    
    // 즉시 상태 초기화 (라우팅보다 먼저 실행하여 UI 즉시 업데이트)
    setLastLoadedCategoryId(null);
    setBreadcrumb([]);
    setCurrentChildren([]);
    setSelectedItemId(null);
    dispatch(setSelectedCategoryId(null));
    
    // 전체 상품 조회 실행
    dispatch(fetchProducts({
      include_category_ids: [],
      page: 1,
      per_page: 20
    }) as any);
    
    // 루트 경로로 라우팅
    const fullPath = `/shopping/`;
    navigate(fullPath, {
      state: {
        categoryId: null,
        categoryName: '전체',
        menuItemId: 'all',
        routingPath: '/'
      }
    });
  }, [navigate, mode, dispatch]);
  
  // props에서 원본 menuItems 추출
  const { menuItems = [] } = componentProps;

  // 외부 스킨을 위한 menuItems 처리
  // breadcrumb이 있으면 currentChildren을 menuItems로 전달 (최하위 시 빈 배열)
  // breadcrumb이 없으면 웹빌더 원본 menuItems 전달
  const effectiveMenuItems = (breadcrumb && breadcrumb.length > 0) ? currentChildren : menuItems;


  return {
    data: {
      menuItems: effectiveMenuItems, // 외부 스킨이 올바른 메뉴를 받도록 조정
      loading,
      error,
      selectedItemId,
      isExpanded,
      breadcrumb,
      currentChildren
    },
    actions: {
      handleMenuItemClick,
      toggleExpanded,
      handleBackToRoot
    }
  };
};

export default useCategoryMenuLogic;